import type { FilterObject } from '@moment/trading-types/filter-expression'
import type { Field } from '@/portfolio/proposal-builder/types'
import type { ModelConstraint } from '@/types/api/constraints'

import { Operator } from '@moment/trading-types/filter'

import { retrieveScope } from '@/portfolio/proposal-builder/config'
import {
  composeScope,
  flattenAndClauses,
  stripNoOpScope,
} from '@/portfolio/shared/constraint/no-op-scope'

export const hasScope = (
  scope: FilterObject | null | undefined,
): scope is FilterObject => scope != null && Object.keys(scope).length > 0

/** The scope to serialize for a constraint: a custom scope wins only where it actually narrows something. */
export function resolveScope(
  scope: FilterObject | null | undefined,
  field: Field,
): FilterObject | undefined {
  return stripNoOpScope(scope) ?? retrieveScope(field)
}

// Whether a field's condition is an allocation exclusion (`{ not_in: [...] }`).
function isAllocationExclusion(condition: unknown): boolean {
  if (condition === null || typeof condition !== 'object') return false
  return (condition as Record<string, unknown>)[Operator.NotIn] != null
}

// Flattens `scope`, dropping the allocation-exclusion keys for `fields` while
// keeping each clause's sibling keys (the API ANDs a clause's top-level keys).
function clausesWithoutAllocationExclusions(
  scope: FilterObject | undefined,
  fields: Field[],
): FilterObject[] {
  return flattenAndClauses(scope).flatMap((clause) => {
    const kept = Object.entries(clause as Record<string, unknown>).filter(
      ([key, condition]) =>
        !fields.includes(key as Field) || !isAllocationExclusion(condition),
    )
    return kept.length > 0 ? [Object.fromEntries(kept) as FilterObject] : []
  })
}

/**
 * Strips allocation exclusions (`{ [field]: { not_in: [...] } }`) for the
 * given fields from a scope. Used when a scope is promoted to the whole
 * risk factor: the exclusion belongs to the diversification constraint alone
 * (re-derived at save time by mergeScopeWithAllocationExclusions) and must
 * not leak onto universe/average-value/allocation constraints via the
 * factor-level fallback.
 */
export function stripAllocationExclusionClauses(
  scope: FilterObject | undefined,
  fields: Field[],
): FilterObject | undefined {
  return composeScope(clausesWithoutAllocationExclusions(scope, fields))
}

/**
 * Layers the allocation-category exclusion for `field` onto a base scope.
 * Idempotent: a stale exclusion already on the scope is replaced rather than
 * nested, so re-serializing an already-serialized scope is stable.
 */
export function mergeScopeWithAllocationExclusions(
  baseScope: FilterObject | undefined,
  field: Field,
  categoriesWithAllocations: string[] | null | undefined,
): FilterObject | undefined {
  // No categories to exclude → leave the scope untouched. Stripping here would
  // also drop a user's own `not_in` scope on this field.
  if (!categoriesWithAllocations || categoriesWithAllocations.length === 0) {
    return baseScope
  }

  const preservedClauses = clausesWithoutAllocationExclusions(baseScope, [
    field,
  ])

  const exclusionFilter: FilterObject = {
    [field]: { [Operator.NotIn]: categoriesWithAllocations },
  }

  return composeScope([...preservedClauses, exclusionFilter])
}

/**
 * The part of a loaded constraint's stored scope that narrows something. An
 * allocation-exclusion clause the save side appended is preserved.
 */
export function extractScope(
  constraint: ModelConstraint,
): FilterObject | undefined {
  return stripNoOpScope('scope' in constraint ? constraint.scope : undefined)
}
